/**
 * Cloudflare R2 Upload Utility - Admin Panel
 * 
 * Firebase Storage yerine Cloudflare R2 Worker üzerinden
 * dosya yükleme, silme ve URL oluşturma işlemlerini yönetir.
 * 
 * Android tarafındaki CloudflareR2Storage.kt ile aynı token mekanizmasını kullanır.
 */

const R2_WORKER_URL = process.env.REACT_APP_R2_WORKER_URL || 'https://pinki-r2-media.gfflortdestek.workers.dev';
const UPLOAD_SECRET = process.env.REACT_APP_R2_UPLOAD_SECRET || 'Pinki.R2.Upload.Secret.2026';
const TOKEN_EXPIRY_MS = 600_000; // 10 dakika

/**
 * HMAC-SHA256 tabanlı upload token oluştur
 * Android'deki generateToken() ile birebir aynı mantık
 */
async function generateToken(path) {
  const expiry = Date.now() + TOKEN_EXPIRY_MS;
  const message = `${path}:${expiry}`;

  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(UPLOAD_SECRET),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(message));
  
  // Base64URL encode (+ → -, / → _, padding kaldır)
  const base64 = btoa(String.fromCharCode(...new Uint8Array(signature)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return `${base64}.${expiry}`;
}

/**
 * Dosyayı R2'ye yükle
 * @param {File} file - Yüklenecek dosya (File nesnesi)
 * @param {string} path - R2'deki hedef yol (örn: "profile_images/uid123/photo.jpg")
 * @param {function} onProgress - İlerleme callback'i (0-100) (opsiyonel)
 * @returns {Promise<string>} Yüklenen dosyanın public URL'si
 */
export async function uploadToR2(file, path, onProgress = null) {
  const token = await generateToken(path);
  const uploadUrl = `${R2_WORKER_URL}/${path}?token=${token}`;

  console.log(`[R2] Uploading: ${path} (${(file.size / 1024).toFixed(1)} KB)`);

  // XMLHttpRequest ile progress tracking (opsiyonel)
  if (onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const publicUrl = `${R2_WORKER_URL}/${path}`;
          console.log(`[R2] Upload success: ${publicUrl}`);
          resolve(publicUrl);
        } else {
          reject(new Error(`Upload failed (${xhr.status}): ${xhr.responseText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload network error'));
      xhr.send(file);
    });
  }

  // Basit fetch ile upload (progress yok)
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload failed (${response.status}): ${errorText}`);
  }

  const publicUrl = `${R2_WORKER_URL}/${path}`;
  console.log(`[R2] Upload success: ${publicUrl}`);
  return publicUrl;
}

/**
 * Blob/ArrayBuffer'dan R2'ye yükle
 * @param {Blob|ArrayBuffer} data - Yüklenecek veri
 * @param {string} path - R2'deki hedef yol
 * @param {string} contentType - MIME tipi
 * @returns {Promise<string>} Public URL
 */
export async function uploadBlobToR2(data, path, contentType = 'application/octet-stream') {
  const token = await generateToken(path);
  const uploadUrl = `${R2_WORKER_URL}/${path}?token=${token}`;

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: data,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload failed (${response.status}): ${errorText}`);
  }

  return `${R2_WORKER_URL}/${path}`;
}

/**
 * R2'den dosya sil
 * @param {string} path - Silinecek dosyanın R2 yolu
 * @returns {Promise<boolean>} Başarılı mı
 */
export async function deleteFromR2(path) {
  const token = await generateToken(path);
  const deleteUrl = `${R2_WORKER_URL}/${path}?token=${token}`;

  try {
    const response = await fetch(deleteUrl, { method: 'DELETE' });
    return response.ok;
  } catch (e) {
    console.error(`[R2] Delete failed: ${e.message}`);
    return false;
  }
}

/**
 * Public URL oluştur (indirme/görüntüleme için)
 */
export function getR2PublicUrl(path) {
  return `${R2_WORKER_URL}/${path}`;
}

/**
 * UUID oluştur (dosya adı için)
 */
export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const R2_WORKER_BASE_URL = R2_WORKER_URL;
